import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import PositiveListService from "src/modules/data-protection/apis/PositiveListService";
import {
    ANALYSIS_POLL_INTERVAL_MS,
    POSITIVE_LIST_ANALYSIS_QUERY_KEY,
    isAnalysisInProgress,
} from "../helpers/analysisStatus";
import { POSITIVE_LIST_QUERY_KEY } from "../helpers/measureList";

const usePositiveListAnalysisStatus = ({ softwareId, t } = {}) => {
    const queryClient = useQueryClient();
    const previousStatusRef = useRef(null);

    const query = useQuery({
        queryKey: [POSITIVE_LIST_ANALYSIS_QUERY_KEY, softwareId],
        queryFn: async () => {
            const service = PositiveListService.getInstance();

            return await service.getAnalysisStatus(softwareId);
        },
        enabled: Boolean(softwareId),
        refetchOnWindowFocus: false,
        refetchInterval: (data) => {
            return isAnalysisInProgress(data?.status)
                ? ANALYSIS_POLL_INTERVAL_MS
                : false;
        },
        onError: (error) => {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }

            toast(
                t
                    ? t("An error occurred while fetching analysis status.")
                    : "An error occurred while fetching analysis status.",
                {
                    type: "error",
                }
            );
        },
    });

    useEffect(() => {
        const status = query.data?.status;
        const previousStatus = previousStatusRef.current;

        if (
            status === "completed"
            && isAnalysisInProgress(previousStatus)
        ) {
            queryClient.invalidateQueries([POSITIVE_LIST_QUERY_KEY, softwareId]);
        }

        previousStatusRef.current = status;
    }, [query.data?.status, queryClient, softwareId]);

    return query;
};

export default usePositiveListAnalysisStatus;
